import { Component, OnInit } from '@angular/core';
import { Destroyable } from '../../../utils/mixins/destroyable.mixin';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SimulatorType } from '../../../core/enums/simulator-type.enum';
import { SimulationsService } from '../../../services/simulations/simulations.service';
import { Router } from '@angular/router';
import { SnackbarService } from '../../../services/snackbar.service';
import { LoadingService } from '../../../services/loading.service';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../../core/consts/api.const';
import { Directions } from '../../../core/models/simulation.model';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { MatButtonModule } from '@angular/material/button';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { filter, map, merge } from 'rxjs';
import { SelectRockFilePopupComponent } from '../../../common/popups/select-rock-file-popup/select-rock-file-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { RockFile } from '../../../core/models/rock-files.model';
import { MatRadioModule } from '@angular/material/radio';
import { RockThumbnailService } from '../../../services/rock-thumbnail/rock-thumbnail.service';
import { RockImagesService } from '../../../services/rock-images/rock-images.service';
import { RockFileLoader } from '../../../utils/rock-file-loader.util';
import { RockPreviewComponent } from '../../../common/rock-preview/rock-preview.component';
import { RemoveExtensionPipe } from '../../../common/pipes/remove-extension.pipe';

@Component({
  selector: 'app-capillary-pressure',
  imports: [
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    ReactiveFormsModule,
    MatRadioModule,
    RockPreviewComponent,
    RemoveExtensionPipe
],
  templateUrl: './capillary-pressure.component.html',
  styleUrl: './capillary-pressure.component.scss',
  standalone: true,
})
export class CapillaryPressureComponent extends Destroyable(Object) implements OnInit {
  simulationDirectionsForm!: FormGroup;
  interfacialTensionForm!: FormGroup;
  contactAngleForm!: FormGroup;
  simulationNameForm!: FormGroup;
  fileName: string = '';
  isLoading: boolean = false;
  thumbnailUrl: string | null = null;
  rockFileMetadata: RockFile | null = null;
  
  private rockFileLoader: RockFileLoader;

  readonly componentId: SimulatorType = SimulatorType.SubsamplePC;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly simulationsService: SimulationsService,
    private readonly router: Router,
    private readonly snackbarService: SnackbarService,
    private readonly loadingService: LoadingService,
    private readonly rockThumbnailService: RockThumbnailService,
    private readonly rockImagesService: RockImagesService,
  ) {
    super();
    this.rockFileLoader = new RockFileLoader(rockThumbnailService, rockImagesService);
  }

  get totalPorosity(): string {
    const fractionHistogram = this.rockFileMetadata?.fractionHistogram;
    if (fractionHistogram && fractionHistogram['0'] != null) {
      const porosity = fractionHistogram['0'];
      return porosity.toFixed(2);
    }
    return 'N/A';
  }

  get nameControl(): FormControl {
    return this.simulationNameForm.get('name') as FormControl;
  }

  get interfacialTension(): FormControl {
    return this.interfacialTensionForm.get('interfacialTension') as FormControl;
  }

  get primaryDrainage(): FormControl {
    return this.contactAngleForm.get('primaryDrainage') as FormControl;
  }

  get firstImbibition(): FormControl {
    return this.contactAngleForm.get('firstImbibition') as FormControl;
  }

  get secondaryDrainage(): FormControl {
    return this.contactAngleForm.get('secondaryDrainage') as FormControl;
  }

  get simulatePrimaryDrainage(): FormControl {
    return this.contactAngleForm.get('simulatePrimaryDrainage') as FormControl;
  }

  get simulateFirstImbibition(): FormControl {
    return this.contactAngleForm.get('simulateFirstImbibition') as FormControl;
  }

  get simulateSecondaryDrainage(): FormControl {
    return this.contactAngleForm.get('simulateSecondaryDrainage') as FormControl;
  }

  get selectedDirection(): FormControl {
    return this.simulationDirectionsForm.get('selectedDirection') as FormControl;
  }

  get simulationDirections(): Directions {
    return {
      xDirection: this.selectedDirection?.value === 'xDirection',
      yDirection: this.selectedDirection?.value === 'yDirection',
      zDirection: this.selectedDirection?.value === 'zDirection',
    }
  }

  ngOnInit(): void {
    this.initForms();
    this.subscribeToLoadingState();
  }

  selectRockFileName(): void {
    const dialogRef = this.dialog.open(SelectRockFilePopupComponent, {
      panelClass: 'large-popup-size',
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean), this.takeUntilDestroyed())
      .subscribe((file: RockFile) => {
        this.fileName = file.fileName;
        this.loadThumbnail(file.fileName);
      });
  }

  private loadThumbnail(fileName: string): void {
    const previousThumbnailUrl = this.thumbnailUrl;

    // Load thumbnail (always works)
    this.rockFileLoader
      .getThumbnail(fileName)
      .pipe(this.takeUntilDestroyed())
      .subscribe((url) => {
        if (url) {
          if (previousThumbnailUrl) {
            this.rockFileLoader.revokeThumbnailUrl(previousThumbnailUrl);
          }
          this.thumbnailUrl = url;
        }
      });

    this.rockFileLoader
      .getMetadata(fileName)
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (metadata) => {
          this.rockFileMetadata = metadata;
        },
        error: () => {
          // Metadata not available, porosity will show N/A
          this.rockFileMetadata = null;
        }
      });
  }

  override ngOnDestroy(): void {
    if (this.thumbnailUrl) {
      this.rockFileLoader.revokeThumbnailUrl(this.thumbnailUrl);
    }
    super.ngOnDestroy();
  }

  runSimulation(): void {
    if (this.isSimulationInputValid()) {
      this.loadingService.startLoading(this.componentId);

      const options = {
        headers: new HttpHeaders({
          [COMPONENT_ID_HEADER]: this.componentId,
        }),
        params: this.generateParams(),
      };

      this.simulationsService
        .addSimulation(this.simulationDirections, this.fileName, options, this.componentId)
        .pipe(this.takeUntilDestroyed())
        .subscribe(() => {
          this.snackbarService.openCustom({
            message: 'Simulation started successfully',
            title: 'Success',
            type: 'success',
            action: 'Close',
          });
          this.fileName = '';

          this.router.navigate(['/dashboard']);
        });
    }
  }

  isSimulationInputValid(): boolean {
    return Boolean(
      this.fileName &&
      this.simulationNameForm.valid &&
      this.interfacialTensionForm.valid &&
      this.contactAngleForm.valid &&
      this.checkIfAtLeastOneDirectionSelected() &&
      this.checkContactAngleSelection(),
    );
  }

  private checkIfAtLeastOneDirectionSelected(): boolean {
    return this.selectedDirection?.value;
  }

  private checkContactAngleSelection(): boolean {
    if (this.simulateFirstImbibition.getRawValue()) {
      return this.isContactAngleValid(this.primaryDrainage.getRawValue()) &&
        this.isContactAngleValid(this.firstImbibition.getRawValue());
    } else if (this.simulatePrimaryDrainage.getRawValue()) {
      return this.isContactAngleValid(this.primaryDrainage.getRawValue());
    } else {
      return false;
    }
  }

  private isContactAngleValid(value: number): boolean {
    return Boolean(value || value === 0);
  }

  private generateParams(): HttpParams {
    const { interfacialTension } = this.interfacialTensionForm.getRawValue();
    const {
      primaryDrainage,
      firstImbibition,
      simulateFirstImbibition,
      secondaryDrainage,
      simulateSecondaryDrainage,
    } = this.contactAngleForm.getRawValue();

    let params: HttpParams = new HttpParams()
      .set('interfactialTension', interfacialTension)
      .set('contactAnglePrimaryDrainage', primaryDrainage)
      .set('simulatePrimaryDrainage', 'true')
      .set('name', this.getSimulationName());

    if (simulateFirstImbibition && this.isContactAngleValid(firstImbibition)) {
      params = params
        .set('contactAngleFirstImbibition', firstImbibition)
        .set('simulateFirstImbibition', simulateFirstImbibition);
    } else {
      params = params.set('simulateFirstImbibition', 'false');
    }

    if (simulateSecondaryDrainage && this.isContactAngleValid(secondaryDrainage)) {
      params = params
        .set('contactAngleSecondaryDrainage', secondaryDrainage)
        .set('simulateSecondaryDrainage', simulateSecondaryDrainage);
    } else {
      params = params.set('simulateSecondaryDrainage', 'false');
    }

    return params;
  }

  private getSimulationName(): string {
    let { name } = this.simulationNameForm.getRawValue();
    return name || (this.fileName ?? '').split('.')[0];
  }

  private initForms(): void {
    this.initSimulationNameForm();
    this.initSimulationDirectionsForm();
    this.initInterfacialTensionForm();
    this.initContactAngleFormForm();
  }

  private initSimulationNameForm(): void {
    this.simulationNameForm = this.fb.group({
      name: ['', [Validators.maxLength(256)]],
    });
  }

  private initSimulationDirectionsForm(): void {
    this.simulationDirectionsForm = this.fb.group({
      selectedDirection: ['xDirection'],
    });
  }

  private initInterfacialTensionForm(): void {
    this.interfacialTensionForm = this.fb.group({
      interfacialTension: [null, [Validators.required, Validators.min(0)]],
    });
  }

  private initContactAngleFormForm(): void {
    this.contactAngleForm = this.fb.group({
      primaryDrainage: [null, [Validators.required, Validators.min(-1), Validators.max(1)]],
      firstImbibition: [{ value: null, disabled: true }, [Validators.min(-1), Validators.max(1)]],
      secondaryDrainage: [{ value: null, disabled: true }, [Validators.min(-1), Validators.max(1)]],
      simulatePrimaryDrainage: [{ value: true, disabled: true }],
      simulateFirstImbibition: [{ value: false, disabled: true }],
      simulateSecondaryDrainage: [{ value: false, disabled: true }],
    });

    merge(
      this.primaryDrainage.valueChanges.pipe(map((value: number) => ({ controlName: 'primaryDrainage', value }))),
      this.firstImbibition.valueChanges.pipe(map((value: number) => ({ controlName: 'firstImbibition', value })))
    )
      .pipe(this.takeUntilDestroyed())
      .subscribe(({ controlName, value }) => {
        this.setContactAngleControlStatus(controlName, value);
      });
  }

  private setContactAngleControlStatus(controlName: string, controlValue: number): void {
    switch (controlName) {
      case 'primaryDrainage': {
        if (this.isContactAngleValid(controlValue)) {
          this.firstImbibition.enable({ emitEvent: false });
          if (this.isContactAngleValid(this.firstImbibition.getRawValue())) {
            this.simulateFirstImbibition.enable({ emitEvent: false });
          }
        } else {
          this.firstImbibition.disable({ emitEvent: false });
          this.simulateFirstImbibition.patchValue(false, { emitEvent: false });
          this.simulateFirstImbibition.disable({ emitEvent: false });
        }
        break;
      }

      case 'firstImbibition': {
        if (this.isContactAngleValid(controlValue)) {
          this.simulateFirstImbibition.enable({ emitEvent: false });
        } else {
          this.simulateFirstImbibition.patchValue(false, { emitEvent: false });
          this.simulateFirstImbibition.disable({ emitEvent: false });
        }
        break;
      }
    }
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((isLoading: boolean) => {
        this.isLoading = isLoading;
      });
  }
}
